/**
 * SOLID Review
 * S — Strong. One concern: a table's seats from the server's id-and-name roster, built fresh (the lobby)
 *     or merged into the seats already held (a game in progress).
 * O — Good. A new default seat field is added once, in `seatFor`, and both paths pick it up.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two exports, each taking exactly what it reads.
 * D — Strong. The Domain's enums and its seat schema type; no store, port or React.
 */
import { PlayerStatus, MonetizationTrack, INITIAL_LIVES } from '../uml-components/primitives';
import type { IEntityClientSeat } from '../ports/schemas/ClientTable';

/**
 * MOVED to the Domain (frontend strict-ports plan, FE1, 2026-10-03). `buildRosterFromRaw` was
 * application/mappers/buildRosterFromRaw.ts, shared by the two lobby roster hooks; `mergeRoster` was
 * ControlPanel's `mergeRoomRoster` (framework/components/game/controlPanel/revealTransforms.ts), which
 * took and returned a whole room. Both now take and return SEATS, so the table decisions and the callers
 * they replace can share them, and the default seat they both spelled out is written once.
 */

/** A roster entry as the server sends it: `roomUpdated`, a `room` read. */
export interface IEntityRosterUpdatePlayer {
  readonly id: string;
  readonly displayName: string;
}

/** A seat the client has not seen before: full lives, ACTIVE, free tier, at its position in the list. */
function seatFor(p: IEntityRosterUpdatePlayer, seatIndex: number, localPlayerId: string | null | undefined): IEntityClientSeat {
  return {
    id: p.id,
    displayName: p.displayName,
    avatarUrl: null,
    status: PlayerStatus.ACTIVE,
    seatIndex,
    isLocalPlayer: p.id === localPlayerId,
    isCurrentTurn: false,
    lives: { current: INITIAL_LIVES, max: INITIAL_LIVES, isEliminated: false },
    monetization: { track: MonetizationTrack.FREE, matchCredits: 0, hasPremiumAccess: false },
  };
}

/** The lobby's roster: every seat fresh, from the server's list. */
export function buildRosterFromRaw(
  players: ReadonlyArray<IEntityRosterUpdatePlayer> | null | undefined,
  localPlayerId: string | null | undefined,
): IEntityClientSeat[] {
  return (players ?? []).map((p, i) => seatFor(p, i, localPlayerId));
}

/**
 * Merge a ROOM_UPDATED roster into the seats we already hold.
 *
 * The rule that matters: a player we ALREADY know is carried over untouched. The update payload
 * is name-and-id only, so rebuilding every entry from it would reset lives, seat index,
 * suspension status and monetization for everyone in the room each time anybody joined — turning
 * a late join into a silent game-state reset for the existing players.
 *
 * New players are seeded at full lives and ACTIVE, which is what the server has just done for
 * them, and their seat index is their position in the incoming list. A player the update no longer
 * lists is dropped.
 */
export function mergeRoster(
  current: ReadonlyArray<IEntityClientSeat>,
  updatedPlayers: ReadonlyArray<IEntityRosterUpdatePlayer> | null | undefined,
  localPlayerId: string | null | undefined,
): IEntityClientSeat[] {
  const existing = new Map(current.map((p) => [p.id, p]));
  return (updatedPlayers ?? []).map((p, i) => existing.get(p.id) ?? seatFor(p, i, localPlayerId));
}
